import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Check, X, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { api, adminHeaders, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WeeklyDigestCard } from "@/components/WeeklyDigestCard";

export const PriceAlertsTab = ({ onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [sources, setSources] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/price-alerts", adminHeaders());
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const check = async () => {
    setChecking(true);
    try {
      const res = await api.post("/admin/price-check", {}, adminHeaders());
      setSources(res.data.sources);
      toast.success(`Checked ${res.data.matched} products against manufacturer pricing.`);
      await load();
    } catch (e) {
      toast.error("Price check failed.");
    } finally {
      setChecking(false);
    }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/admin/price-alerts/${id}/${action}`, {}, adminHeaders());
      setRows((r) => r.filter((x) => x.id !== id));
      onChanged();
    } catch (e) {
      toast.error("Action failed.");
    }
  };

  const acceptAll = async () => {
    if (!window.confirm(`Update ${rows.length} prices to the manufacturer's current price?`)) return;
    const res = await api.post("/admin/price-alerts/accept-all", {}, adminHeaders());
    toast.success(`${res.data.updated} prices updated.`);
    setRows([]);
    onChanged();
  };

  return (
    <div>
      <WeeklyDigestCard onSent={load} />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            Products whose manufacturer list price differs from yours. Accept to update your price, or dismiss to
            hide until the manufacturer changes it again.
          </p>
          {sources && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="price-check-sources">
              Sources: {Object.entries(sources).map(([k, v]) => `${k} — ${v}`).join(" · ")}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="price-check-button" onClick={check} disabled={checking} className="font-bold">
            {checking ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <RefreshCw size={16} className="mr-1.5" />}
            Check manufacturer prices
          </Button>
          {rows.length > 0 && (
            <Button data-testid="price-accept-all-button" onClick={acceptAll} className="rounded-md bg-[#1B66C9] font-bold hover:bg-[#144E9C]">
              Accept all ({rows.length})
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="py-10 text-center text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-muted-foreground" data-testid="price-alerts-empty">
          All your prices match the manufacturers' current list prices.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm" data-testid="price-alerts-table">
            <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3 font-bold">Product</th>
                <th className="p-3 font-bold">My price</th>
                <th className="p-3 font-bold">Manufacturer</th>
                <th className="p-3 font-bold">Change</th>
                <th className="p-3 font-bold">Source</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`price-alert-${r.id}`} className="border-t border-border">
                  <td className="p-3">
                    <span className="font-semibold">{r.name}</span>
                    <span className="block text-xs text-muted-foreground">{[r.brand, r.series].filter(Boolean).join(" · ")}</span>
                  </td>
                  <td className="p-3 font-semibold">{money(r.price)}</td>
                  <td className="p-3 font-semibold">{money(r.mfr_price)}</td>
                  <td className={`p-3 font-bold ${r.diff > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>
                    <span className="flex items-center gap-1">
                      {r.diff > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      {r.diff > 0 ? "+" : "−"}{money(Math.abs(r.diff))}
                      {r.price > 0 && <span className="text-xs font-normal text-muted-foreground">({((r.diff / r.price) * 100).toFixed(1)}%)</span>}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{r.source}</td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button
                        data-testid={`price-accept-${r.id}`}
                        onClick={() => act(r.id, "accept")}
                        title="Use manufacturer price"
                        className="flex h-8 items-center gap-1 rounded-md bg-[#16A34A]/10 px-2.5 text-xs font-bold text-[#16A34A] hover:bg-[#16A34A]/20"
                      >
                        <Check size={14} /> Accept
                      </button>
                      <button
                        data-testid={`price-dismiss-${r.id}`}
                        onClick={() => act(r.id, "dismiss")}
                        title="Keep my price"
                        className="flex h-8 items-center gap-1 rounded-md px-2.5 text-xs font-bold text-muted-foreground hover:bg-secondary"
                      >
                        <X size={14} /> Keep mine
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
